import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  where, // Import where
} from 'firebase/firestore';
import { firestore, functions, auth } from '../firebase'; // Import functions instance
import { httpsCallable } from 'firebase/functions'; // Import httpsCallable
import {
  BusinessHours,
  ServiceProvider,
  ServiceLocationInfo,
  ServiceVerificationStatus,
  ServiceProviderProfileImage,
  ServiceProviderProfileImageMutation,
  ServiceAvailabilityOverride,
  ServiceProviderPricing,
  ServiceCapabilities,
} from '../types/firestore';
import type { ServiceProviderFulfillmentConfig } from '../shared/fulfillment';
import {
  isDiscoverableServiceProvider,
  isOperationalServiceProvider,
} from '../utils/providerEligibility';
import { getApprovedSpecializationCatalog } from './specializationCatalogService';
import { normalizeProviderSpecializationFields } from '../utils/specializationCatalog';
import {
  resolveServiceProviderImageStoragePath,
  resolveServiceProviderImageUrl,
} from '../utils/serviceProviderImage';
import { cloneServiceCapabilities } from '../utils/serviceCapabilities';

// Define the expected input type for the Cloud Function, matching the backend definition
interface SaveProfileRequestData {
  serviceName?: string;
  description?: string;
  phoneNumber?: string;
  address?: { street: string; city: string; postalCode: string };
  location?: { latitude: number; longitude: number } | null;
  businessHours?: BusinessHours | null;
  availabilityOverride?: ServiceAvailabilityOverride | null;
  servicePricing?: ServiceProviderPricing | null;
  website?: string | null;
  photos?: string[];
  specializations?: string[];
  profileImage?: ServiceProviderProfileImageMutation | null;
  serviceFulfillment?: ServiceProviderFulfillmentConfig | null;
  serviceCapabilities?: ServiceCapabilities | null;
  submitForVerification?: boolean;
}

// Define the expected success response type from the Cloud Function
interface SaveProfileResponse {
  success: boolean;
  message: string;
  profileId?: string;
  verificationStatus?: ServiceVerificationStatus;
  profileImage?: ServiceProviderProfileImage | null;
}

const stripUndefinedValues = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedValues(item)) as T;
  }
  if (value && Object.prototype.toString.call(value) === '[object Object]') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([entryKey, entryValue]) => [entryKey, stripUndefinedValues(entryValue)])
    ) as T;
  }
  return value;
};

interface GeoCoordinatePair {
  latitude: number;
  longitude: number;
}

interface DiscoverySearchSuggestions {
  specializations: string[];
  categories: string[];
  serviceProviderNames: string[];
}

const serviceProviderProfileCache = new Map<string, ServiceProvider | null>();

const cloneServiceProviderProfile = (profile: ServiceProvider | null): ServiceProvider | null => {
  if (!profile) return null;
  const clonedProfile: ServiceProvider = {
    ...profile,
    address: profile.address ? { ...profile.address } : profile.address,
    photos: Array.isArray(profile.photos) ? [...profile.photos] : [],
    specializations: Array.isArray(profile.specializations) ? [...profile.specializations] : [],
    offeredCategories: Array.isArray(profile.offeredCategories)
      ? [...profile.offeredCategories]
      : profile.offeredCategories,
    businessHours: profile.businessHours
      ? {
          ...profile.businessHours,
          weekly: { ...profile.businessHours.weekly },
        }
      : profile.businessHours,
    availabilityOverride: profile.availabilityOverride
      ? { ...profile.availabilityOverride }
      : profile.availabilityOverride,
    servicePricing: profile.servicePricing
      ? {
          ...profile.servicePricing,
          hourlyRate: profile.servicePricing.hourlyRate
            ? { ...profile.servicePricing.hourlyRate }
            : profile.servicePricing.hourlyRate,
        }
      : profile.servicePricing,
    serviceFulfillment: profile.serviceFulfillment
      ? {
          ...profile.serviceFulfillment,
          modes: profile.serviceFulfillment.modes
            ? { ...profile.serviceFulfillment.modes }
            : profile.serviceFulfillment.modes,
          payment: profile.serviceFulfillment.payment
            ? { ...profile.serviceFulfillment.payment }
            : profile.serviceFulfillment.payment,
        }
      : profile.serviceFulfillment,
    serviceCapabilities: cloneServiceCapabilities(profile.serviceCapabilities),
  };

  if (Object.prototype.hasOwnProperty.call(profile, 'profileImage')) {
    clonedProfile.profileImage = profile.profileImage
      ? {
          ...profile.profileImage,
          full: { ...profile.profileImage.full },
          thumb: { ...profile.profileImage.thumb },
        }
      : profile.profileImage;
  }

  return clonedProfile;
};

const createEmptyProfileForCache = (uid: string): ServiceProvider => ({
  uid,
  serviceName: '',
  description: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
  },
  location: null,
  phoneNumber: '',
  email: String(auth.currentUser?.email || '').trim(),
  website: null,
  businessHours: null,
  availabilityOverride: null,
  photos: [],
  specializations: [],
  offeredCategories: [],
  serviceCapabilities: null,
  averageRating: 0,
  numberOfRatings: 0,
  isVerified: false,
  verificationStatus: 'profile_incomplete',
  createdAt: new Date(),
  updatedAt: new Date(),
});

const getCachedServiceProviderProfile = (uid: string): ServiceProvider | null | undefined => {
  const normalizedUid = String(uid || '').trim();
  if (!normalizedUid || !serviceProviderProfileCache.has(normalizedUid)) {
    return undefined;
  }
  return cloneServiceProviderProfile(serviceProviderProfileCache.get(normalizedUid) ?? null);
};

const setCachedServiceProviderProfile = (uid: string, profile: ServiceProvider | null): void => {
  const normalizedUid = String(uid || '').trim();
  if (!normalizedUid) return;
  serviceProviderProfileCache.set(normalizedUid, cloneServiceProviderProfile(profile));
};

const upsertCachedServiceProviderProfile = (
  uid: string,
  updater: (current: ServiceProvider | null) => ServiceProvider | null
): void => {
  const normalizedUid = String(uid || '').trim();
  if (!normalizedUid) return;
  const current = cloneServiceProviderProfile(
    serviceProviderProfileCache.get(normalizedUid) ?? null
  );
  serviceProviderProfileCache.set(normalizedUid, cloneServiceProviderProfile(updater(current)));
};

const syncCacheAfterProfileSave = (
  uid: string,
  profileData: SaveProfileRequestData,
  verificationStatus?: ServiceVerificationStatus,
  profileImage?: ServiceProviderProfileImage | null
): void => {
  upsertCachedServiceProviderProfile(uid, (current) => {
    const baseProfile = current || createEmptyProfileForCache(uid);
    const nextVerificationStatus =
      verificationStatus || baseProfile.verificationStatus || 'profile_incomplete';
    const nextIsVerified = nextVerificationStatus === 'approved';
    const hasBaseProfileImage = Object.prototype.hasOwnProperty.call(baseProfile, 'profileImage');
    const nextProfileImage =
      profileImage !== undefined
        ? profileImage
        : hasBaseProfileImage
          ? (baseProfile.profileImage ?? null)
          : undefined;
    const nextProfile: ServiceProvider = {
      ...baseProfile,
      serviceName: String(profileData.serviceName || baseProfile.serviceName || '').trim(),
      description: String(profileData.description || baseProfile.description || '').trim(),
      address: {
        street: String(profileData.address?.street || baseProfile.address?.street || '').trim(),
        city: String(profileData.address?.city || baseProfile.address?.city || '').trim(),
        postalCode: String(
          profileData.address?.postalCode || baseProfile.address?.postalCode || ''
        ).trim(),
      },
      location: profileData.location
        ? {
            latitude: profileData.location.latitude,
            longitude: profileData.location.longitude,
          }
        : baseProfile.location,
      phoneNumber: String(profileData.phoneNumber || baseProfile.phoneNumber || '').trim(),
      email: String(baseProfile.email || auth.currentUser?.email || '').trim(),
      website:
        profileData.website !== undefined ? profileData.website : baseProfile.website || null,
      businessHours:
        profileData.businessHours !== undefined
          ? profileData.businessHours
          : baseProfile.businessHours || null,
      availabilityOverride:
        profileData.availabilityOverride !== undefined
          ? profileData.availabilityOverride
          : baseProfile.availabilityOverride || null,
      servicePricing:
        profileData.servicePricing !== undefined
          ? profileData.servicePricing
          : baseProfile.servicePricing,
      serviceFulfillment:
        profileData.serviceFulfillment !== undefined
          ? profileData.serviceFulfillment
          : baseProfile.serviceFulfillment,
      serviceCapabilities:
        profileData.serviceCapabilities !== undefined
          ? cloneServiceCapabilities(profileData.serviceCapabilities)
          : cloneServiceCapabilities(baseProfile.serviceCapabilities),
      photos: Array.isArray(profileData.photos) ? profileData.photos : baseProfile.photos || [],
      specializations: Array.isArray(profileData.specializations)
        ? profileData.specializations
        : baseProfile.specializations || [],
      verificationStatus: nextVerificationStatus,
      isVerified: nextIsVerified,
      updatedAt: new Date(),
    } as ServiceProvider;

    if (nextProfileImage !== undefined) {
      nextProfile.profileImage = nextProfileImage
        ? {
            ...nextProfileImage,
            full: { ...nextProfileImage.full },
            thumb: { ...nextProfileImage.thumb },
          }
        : nextProfileImage;
    } else {
      delete (nextProfile as Partial<ServiceProvider>).profileImage;
    }

    return nextProfile;
  });
};

const syncCacheAfterSubmitForVerification = (uid: string): void => {
  upsertCachedServiceProviderProfile(uid, (current) => {
    const baseProfile = current || createEmptyProfileForCache(uid);
    return {
      ...baseProfile,
      verificationStatus: 'pending_approval',
      isVerified: false,
      verificationReviewReason: null,
      verificationReviewedAt: null,
      verificationSubmittedAt: new Date(),
      updatedAt: new Date(),
    } as ServiceProvider;
  });
};

const isFiniteCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isValidCoordinateRange = (latitude: number, longitude: number): boolean =>
  latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

const extractCoordinates = (rawLocation: unknown): GeoCoordinatePair | null => {
  if (!rawLocation || typeof rawLocation !== 'object') return null;

  const candidate = rawLocation as Record<string, unknown>;
  const latitudeCandidates = [candidate.latitude, candidate.lat, candidate._latitude];
  const longitudeCandidates = [
    candidate.longitude,
    candidate.lng,
    candidate.lon,
    candidate._longitude,
  ];

  const latitude = latitudeCandidates.find(isFiniteCoordinate);
  const longitude = longitudeCandidates.find(isFiniteCoordinate);

  if (latitude === undefined || longitude === undefined) return null;
  if (!isValidCoordinateRange(latitude, longitude)) return null;

  return { latitude, longitude };
};

/**
 * Calls the 'saveServiceProviderProfile' Cloud Function to create or update a profile.
 * @param profileData - The profile data to save, matching the SaveProfileRequestData interface.
 * @returns Promise resolving with the success response or rejecting with an error.
 */
const saveProfile = async (profileData: SaveProfileRequestData): Promise<SaveProfileResponse> => {
  try {
    if (auth.currentUser) {
      // Refresh token so backend sees the latest email_verified flag.
      await auth.currentUser.getIdToken(true);
    }
    const sanitizedProfileData = stripUndefinedValues(profileData);
    console.log('[saveProfile Service] Calling Cloud Function with data:', sanitizedProfileData);
    const saveServiceProviderProfileFn = httpsCallable<SaveProfileRequestData, SaveProfileResponse>(
      functions,
      'saveServiceProviderProfile'
    );
    const result = await saveServiceProviderProfileFn(sanitizedProfileData);
    console.log('[saveProfile Service] Cloud Function returned:', result.data);
    // Ensure result.data matches the expected structure
    if (
      typeof result.data !== 'object' ||
      result.data === null ||
      typeof (result.data as any).success !== 'boolean'
    ) {
      throw new Error('Nieprawidłowa odpowiedź z funkcji Cloud.');
    }
    const typedResult = result.data as SaveProfileResponse;
    const uid = String(auth.currentUser?.uid || '').trim();
    if (uid) {
      syncCacheAfterProfileSave(
        uid,
        sanitizedProfileData,
        typedResult.verificationStatus,
        typedResult.profileImage
      );
    }
    return typedResult; // Return the data part of the HttpsCallableResult
  } catch (error: any) {
    console.error(
      '[saveProfile Service] Error calling saveServiceProviderProfile function:',
      error
    );

    const toErrorWithCode = (message: string): Error & { code?: string } => {
      const wrapped = new Error(message) as Error & { code?: string };
      wrapped.code = String(error?.code || '');
      return wrapped;
    };

    // Handle HttpsError by checking error properties (code and message)
    // as HttpsError might not be directly exportable/usable with instanceof
    if (error.code && error.message) {
      console.error(
        `[saveProfile Service] Firebase Function Error: Code=${error.code}, Message=${error.message}`
      );
      const code = String(error.code);
      if (code.includes('permission-denied')) {
        throw toErrorWithCode('Nie masz uprawnien do zapisu profilu serwisu.');
      }
      if (code.includes('unauthenticated')) {
        throw toErrorWithCode('Sesja wygasla. Zaloguj sie ponownie.');
      }
      if (code.includes('invalid-argument')) {
        throw toErrorWithCode(
          error.message || 'Nieprawidlowe dane profilu. Sprawdz formularz i sprobuj ponownie.'
        );
      }
      if (code.includes('failed-precondition')) {
        throw toErrorWithCode('Ta akcja nie jest teraz dostępna. Odśwież dane i spróbuj ponownie.');
      }
      if (code.includes('resource-exhausted')) {
        throw toErrorWithCode('Limit usługi został chwilowo wyczerpany. Spróbuj ponownie później.');
      }
      if (code.includes('internal') || code.includes('unavailable')) {
        throw toErrorWithCode(
          'Wystąpił chwilowy problem po stronie serwera. Spróbuj ponownie za chwilę.'
        );
      }
      throw toErrorWithCode('Nie udalo sie zapisac profilu serwisu. Sprobuj ponownie za chwile.');
    } else {
      // Handle generic errors
      throw new Error('Wystąpił nieznany błąd podczas zapisywania profilu.');
    }
  }
};

const submitProfileForVerification = async (): Promise<{ success: boolean }> => {
  const submitFn = httpsCallable<undefined, { success: boolean }>(
    functions,
    'submitServiceProviderForVerification'
  );
  const result = await submitFn();
  const uid = String(auth.currentUser?.uid || '').trim();
  if (uid && result.data?.success === true) {
    syncCacheAfterSubmitForVerification(uid);
  }
  return result.data;
};

/**
 * Pobiera podstawowe dane lokalizacyjne aktywnych (mających lokalizację) serwisów.
 * @returns Promise<ServiceLocationInfo[]> - Tablica informacji o lokalizacji serwisów.
 */
const getActiveServiceProvidersLocations = async (): Promise<ServiceLocationInfo[]> => {
  const providersCol = collection(firestore, 'serviceProviders');
  // Public discovery must rely only on public serviceProviders fields.
  // Do not read private users/{uid} documents in this flow.
  const q = query(
    providersCol,
    where('verificationStatus', '==', 'approved'),
    where('isVerified', '==', true),
    where('location', '!=', null)
  );

  try {
    const [querySnapshot, approvedSpecializations] = await Promise.all([
      getDocs(q),
      getApprovedSpecializationCatalog(),
    ]);
    const mapped = querySnapshot.docs.map((providerDoc) => {
      const data = providerDoc.data() as ServiceProvider;
      if (!isDiscoverableServiceProvider(data)) {
        return null;
      }
      const normalizedSpecializations = normalizeProviderSpecializationFields(
        {
          specializations: data.specializations,
          offeredCategories: data.offeredCategories,
        },
        approvedSpecializations.index
      );

      const reviewsCount = data.reviewsCount ?? data.numberOfRatings ?? 0;
      const averageRating = data.averageRating ?? 0;
      const imageUrl = resolveServiceProviderImageUrl(data, 'thumb') ?? undefined;
      const imageStoragePath = resolveServiceProviderImageStoragePath(data, 'thumb') ?? undefined;

      const coordinates = extractCoordinates(data.location);
      if (coordinates) {
        const serviceName = data.serviceName || (data as any).companyName || 'Serwis testowy';
        const street = data.address?.street || (data as any).address?.text || '';
        const city = data.address?.city || (data as any).address?.city || '';
        const composedAddress = street && city ? `${street}, ${city}` : street || city || undefined;
        const phone = data.phoneNumber || (data as any).contactPhone;
        return {
          uid: providerDoc.id,
          serviceName,
          position: [coordinates.latitude, coordinates.longitude],
          averageRating,
          reviewsCount,
          numberOfRatings: reviewsCount,
          ratingSum: data.ratingSum ?? 0,
          specializations: normalizedSpecializations.specializations,
          offeredCategories: normalizedSpecializations.offeredCategories,
          description: data.description,
          address: composedAddress,
          businessHours: data.businessHours ?? null,
          availabilityOverride: data.availabilityOverride ?? null,
          servicePricing: data.servicePricing ?? null,
          serviceFulfillment: data.serviceFulfillment ?? null,
          serviceCapabilities: cloneServiceCapabilities(data.serviceCapabilities),
          isVerified: data.isVerified === true,
          imageUrl,
          imageStoragePath,
          phoneNumber: phone,
          isTestData: data.isTestData === true,
        };
      } else {
        console.log(
          `Serwis ${providerDoc.id} pominięty - brak prawidłowej lokalizacji:`,
          data.location
        );
        return null;
      }
    });
    const locations = mapped.filter((item) => item !== null) as ServiceLocationInfo[];
    console.log(`Pobrano ${locations.length} aktywnych lokalizacji serwisów.`);
    return locations;
  } catch (error) {
    console.error('Błąd podczas pobierania lokalizacji serwisów:', error);
    throw error;
  }
};

const getDiscoverySearchSuggestions = async (): Promise<DiscoverySearchSuggestions> => {
  const [services, approvedSpecializations] = await Promise.all([
    getActiveServiceProvidersLocations(),
    getApprovedSpecializationCatalog(),
  ]);

  const specializations = Array.from(
    new Set(approvedSpecializations.entries.map((entry) => entry.name))
  ).sort((a, b) => a.localeCompare(b, 'pl'));

  const categories = Array.from(
    new Set(approvedSpecializations.entries.map((entry) => entry.category))
  ).sort((a, b) => a.localeCompare(b, 'pl'));

  const serviceProviderNames = Array.from(
    new Set(
      services
        .filter((service) => service.isTestData !== true)
        .map((service) => String(service.serviceName || '').trim())
        .filter((serviceName) => serviceName.length > 0)
    )
  ).sort((a, b) => a.localeCompare(b, 'pl'));

  return {
    specializations,
    categories,
    serviceProviderNames,
  };
};

/**
 * Pobiera pełny profil dostawcy usług na podstawie jego UID.
 * @param uid - UID dostawcy usług.
 * @returns Promise<ServiceProvider | null> - Pełny profil serwisu lub null, jeśli nie znaleziono.
 */
const getServiceProviderProfile = async (
  uid: string,
  options?: { forceRefresh?: boolean }
): Promise<ServiceProvider | null> => {
  if (!uid) {
    console.warn('getServiceProviderProfile: uid jest pusty.');
    return null;
  }

  const normalizedUid = String(uid || '').trim();
  if (!options?.forceRefresh) {
    const cached = getCachedServiceProviderProfile(normalizedUid);
    if (cached !== undefined) {
      return cached;
    }
  }

  const profileDocRef = doc(firestore, 'serviceProviders', uid);

  try {
    const docSnap = await getDoc(profileDocRef);
    if (docSnap.exists()) {
      const profile = { ...docSnap.data(), uid: docSnap.id } as ServiceProvider;
      setCachedServiceProviderProfile(normalizedUid, profile);
      return cloneServiceProviderProfile(profile);
    } else {
      console.log(`Nie znaleziono profilu serwisu dla UID: ${uid}`);
      setCachedServiceProviderProfile(normalizedUid, null);
      return null;
    }
  } catch (error) {
    console.error(`Błąd podczas pobierania profilu serwisu ${uid}:`, error);
    throw error;
  }
};

/**
 * Public read-model adapter for operational provider profile.
 * Returns null when provider is not eligible for public/operational flows.
 */
const getPublicOperationalServiceProviderProfile = async (
  uid: string
): Promise<ServiceProvider | null> => {
  const profile = await getServiceProviderProfile(uid);
  if (!profile) return null;
  return isOperationalServiceProvider(profile) ? profile : null;
};

/**
 * Public read-model adapter for discoverable provider profile.
 * Allows viewing test providers in public UI while keeping request flow blocked elsewhere.
 */
const getPublicDiscoverableServiceProviderProfile = async (
  uid: string
): Promise<ServiceProvider | null> => {
  const profile = await getServiceProviderProfile(uid);
  if (!profile) return null;
  return isDiscoverableServiceProvider(profile) ? profile : null;
};

export const serviceProviderService = {
  saveProfile,
  submitProfileForVerification,
  getActiveServiceProvidersLocations,
  getDiscoverySearchSuggestions,
  getServiceProviderProfile,
  getCachedServiceProviderProfile,
  setCachedServiceProviderProfile,
  getPublicOperationalServiceProviderProfile,
  getPublicDiscoverableServiceProviderProfile,
};
